import { Box, Button, Card, Typography, useMediaQuery } from "@mui/material";
import { AlertStyles } from "./AlertStyles";
import moment from "moment";
import { dateFormat } from "../../../../constants/Constants";
import { currencyFormat, isFeatureAvailable } from "../../../../utils/Utility";
import { IAlert } from "../../../../models/user/alert/alert";
import CustomIcon from "../../../../common/CustomIcon/CustomIcon";
import { useAppSelector } from "../../../../store/hooks";
import { RootState } from "../../../../store/store";
import { useTranslation } from "react-i18next";

interface Props {
  alert: IAlert;
  handlePayNow?: (amount: string, carrierPolicyNumber: string) => void;
}
const Alert: React.FC<Props> = ({ alert, handlePayNow }) => {
  const isMobile = useMediaQuery("(max-width:768px)");

  const { t } = useTranslation();

  const { features } = useAppSelector((state: RootState) => state.features);
  return (
    <Card
      sx={AlertStyles.attentionCard}
      id={`customerdashboard_alertcard-${alert.carrierPolicyNo}`}
      data-test-id={`test-customerdashboard_alertcard-${alert.carrierPolicyNo}`}
      aria-label="Alert card for policy"
    >
      <CustomIcon iconName="Attention" />
      <Box
        sx={AlertStyles.attentionCardItem}
        id={`customerdashboard_alertcard-details-${alert.carrierPolicyNo}`}
        data-test-id={`test-customerdashboard_alertcard-details-${alert.carrierPolicyNo}`}
        aria-label={`Alert details for policy ${alert.carrierPolicyNo}`}
      >
        {alert.isLegalNotice ? (
          <Typography
            sx={{ ...AlertStyles.attentionCardItemHeadingStyle, color: "red" }}
            id={`customerdashboard_alertcard-cancellation-${alert.carrierPolicyNo}`}
            data-test-id={`test-customerdashboard_alertcard-cancellation-${alert.carrierPolicyNo}`}
            aria-label="Cancellation notice"
          >
            {t("Cancellation Notice")}
          </Typography>
        ) : (
          <></>
        )}
        <Typography
          sx={AlertStyles.attentionCardItemHeadingStyle}
          id={`customerdashboard_alertcard-policyinfo-${alert.carrierPolicyNo}`}
          data-test-id={`test-customerdashboard_alertcard-policyinfo-${alert.carrierPolicyNo}`}
          aria-label="Policy and insured information"
        >
          {`${alert.carrierPolicyNo} | ${alert.insuredName}`}
        </Typography>
        {alert.dueAmount && alert.dueDate ? (
          <Box
            sx={{
              display: "flex",
              "@media (max-width:768px)": {
                flexDirection: "column",
              },
            }}
            id={`customerdashboard_alertcard-duedetails-${alert.carrierPolicyNo}`}
            data-test-id={`test-customerdashboard_alertcard-duedetails-${alert.carrierPolicyNo}`}
            aria-label="Due amount and date"
          >
            <Typography
              sx={AlertStyles.attentionCardItemContentStyle}
              id={`customerdashboard_alertcard-dueamount-${alert.carrierPolicyNo}`}
              data-test-id={`test-customerdashboard_alertcard-dueamount-${alert.carrierPolicyNo}`}
              aria-label="Due amount"
            >
              {t("Due Amount")}:{" "}
              {currencyFormat("us", +parseFloat(alert.dueAmount).toFixed(2))}
              {isMobile || !alert.dueDate ? "" : "|"}
            </Typography>
            <Typography
              sx={AlertStyles.attentionCardItemContentStyle}
              id={`customerdashboard_alertcard-duedate-${alert.carrierPolicyNo}`}
              data-test-id={`test-customerdashboard_alertcard-duedate-${alert.carrierPolicyNo}`}
              aria-label="Due date"
            >
              {t("Due Date")} : {moment(alert.dueDate).format(dateFormat)}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
        {alert.isRefundAvailable && alert.refund ? (
          <Box
            sx={{
              display: "flex",
              "@media (max-width:768px)": {
                flexDirection: "column",
              },
            }}
            id={`customerdashboard_alertcard-refunddetails-${alert.carrierPolicyNo}`}
            data-test-id={`test-customerdashboard_alertcard-refunddetails-${alert.carrierPolicyNo}`}
            aria-label="Refund amount and date"
          >
            <Typography sx={AlertStyles.attentionCardItemContentStyle}>
              {t("Refund amount")} :{" "}
              {currencyFormat(
                "us",
                +parseFloat(alert.refund.amount).toFixed(2)
              )}
              {isMobile || !alert.refund.date ? "" : "|"}
            </Typography>
            <Typography sx={AlertStyles.attentionCardItemContentStyle}>
              {t("Refund Date")} :{" "}
              {moment(alert.refund.date).format(dateFormat)}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
      </Box>
      {isFeatureAvailable({
        featureCodes: [`lob_${alert?.lobCode?.toLowerCase()}_billing_payments`],
        features,
      }) &&
      alert.dueAmount &&
      alert.dueDate ? (
        <Box sx={AlertStyles.attentionCardButtonContainer}>
          <Button
            id={`customerdashboard_button-paynow-${alert.carrierPolicyNo}`}
            data-test-id={`test-customerdashboard_button-paynow-${alert.carrierPolicyNo}`}
            aria-label={`Click to pay now for policy ${alert.carrierPolicyNo}`}
            variant="contained"
            sx={AlertStyles.attentionCardButton}
            onClick={() =>
              handlePayNow &&
              alert.dueAmount &&
              alert.carrierPolicyNo &&
              handlePayNow(alert.dueAmount, alert.carrierPolicyNo)
            }
          >
            {t("Pay Now")}
          </Button>
        </Box>
      ) : (
        <></>
      )}
    </Card>
  );
};

export default Alert;
